import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { RouteConfig } from 'src/types/route-config.types';
import { QueryState } from "src/types/query-state";
import qs from "qs";
import axios, { AxiosError } from "axios";
import { createContext } from "react";
import { QueryResponseContextProps } from "src/types/query-state.type";
import { UseFormReturn } from "react-hook-form";
import { ZodObject } from "zod";
import { ApiError } from "./api/ApiError";
import { uploadFileToServer } from "./api/public/public.api";
import { ApiResponse } from "./api/axios.interceptor";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getDefaultRouteForRole = (
  role: string,
  routes: RouteConfig[]
): string => {
  const defaultRoute = routes.find(route =>
    route?.isDefaultFor?.includes(role)
  );

  return defaultRoute?.path || '/dashboard';
};

export const getDefaultRoute = (
  routes: RouteConfig[]
): string => {
  const defaultRoute = routes.find(route =>
    route?.isDefaultFor && route?.isDefaultFor?.includes("*")
  );

  return defaultRoute?.path || '/auth/login';
};


export const startCase = (string: any) => {
  return string
    ?.replace(/[\W_]+/g, " ")
    .replace(/(?<=[a-z])([A-Z])/g, " $1")
    .replace(/^[a-z]/, (match: any) => match.toUpperCase());
};

export const camelCase = (string: any) => {
  return string?.replace(/[\W_]+(.|$)/g, (_: any, chr: any) => " " + chr.toUpperCase()).trim();
};

export function convertToCamelCase(str: any) {
  return startCase(camelCase(str));
}


export function getInitials(name: string) {
  if (!name || typeof name !== 'string') {
    return "";
  }
  const words = name.split(' ').filter(word => word.length > 0);
  if (words.length === 0) {
    return "";
  }
  const firstLetters = words.map(word => word[0]);
  return firstLetters.join('').toUpperCase();
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== "";
}


export function stringifyRequestQuery(state: QueryState): string {
  const pagination = qs.stringify(state, { filter: ["page", "items_per_page"], skipNulls: true });
  const sort = qs.stringify(state, { filter: ["sort", "order"], skipNulls: true });
  const search = isNotEmpty(state.search) ? qs.stringify(state, { filter: ["search"], skipNulls: true }) : "";

  const filter = state.filter
    ? Object.entries(state.filter as Object)
      .filter((obj) => isNotEmpty(obj[1]))
      .map((obj) => {
        return `${obj[0]}=${obj[1]}`;
      })
      .join("&")
    : "";

  const _fkey = qs.stringify(state, { filter: ["_fkey"], skipNulls: true });
  const _level = qs.stringify(state, { filter: ["_level"], skipNulls: true });
  const name = qs.stringify(state, { filter: ["name"], skipNulls: true });

  const _f =
    state.f && Object.keys(state.f).length > 0
      ? Object.entries(state.f as Object)
        .filter((obj) => isNotEmpty(obj[1]))
        .map((obj) => {
          return `${obj[0]}:${obj[1]}`;
        })
        .join(";")
      : "";

  const final_f = state.f && _f && Object.values(state.f).every((o: any) => o !== undefined) ? `f=${_f}` : "";

  const _q =
    state.q && Object.keys(state?.q).length > 0
      ? Object.entries(state.q as Object)
        .filter((obj) => isNotEmpty(obj[1]))
        .map((obj) => {
          return `${obj[0]}:${obj[1]}`;
        })
        .join(";")
      : "";

  const final_q =
    state.q && Object.keys(state?.q).length > 0 && Object.values(state.q).every((o: any) => o !== undefined)
      ? `q=${_q}`
      : "";

  return [pagination, sort, search, filter, name, _fkey, _level, final_f, final_q].filter((f) => f !== "").join("&");
}

export function parseRequestQuery(query: string): QueryState {
  const cache: unknown = qs.parse(query);
  return cache as QueryState;
}

export function stringifyObjectToRequestQuery(object: any): string {
  const filter = object
    ? Object.entries(object as Object)
      .filter((obj) => isNotEmpty(obj[1]))
      .map((obj) => {
        return `${obj[0]}=${obj[1]}`;
      })
      .join("&")
    : "";

  return [filter].filter((f) => f).join("&");
  // .toLowerCase()
}


export function formatIndianAmount(value: string | number, decimalPlaces: number = 0): string {
  // Handle null, undefined, empty string
  if (value === null || value === undefined || value === '') return '-';

  // Remove commas to handle formatted inputs
  const numStr = value.toString().replace(/,/g, '');
  const num = parseFloat(numStr);

  // Handle NaN
  if (isNaN(num)) return '';

  // Handle negative numbers → return 0
  if (num < 0) {
    return formatZero(decimalPlaces);
  }

  // Handle very small numbers (e.g., 1e-10, 0.0000001) → return 0
  // Threshold: if number is smaller than what can be displayed with given decimal places
  const threshold = Math.pow(10, -(decimalPlaces + 1));
  if (num > 0 && num < threshold) {
    return formatZero(decimalPlaces);
  }

  // Use epsilon-based rounding to avoid floating point issues
  const multiplier = Math.pow(10, decimalPlaces);
  const roundedNum = Math.round((num + Number.EPSILON) * multiplier) / multiplier;

  // If rounded to 0, return formatted zero
  if (roundedNum === 0) {
    return formatZero(decimalPlaces);
  }

  // Format using Indian locale with specified decimal places
  return roundedNum.toLocaleString('en-IN', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
}


function formatZero(decimalPlaces: number): string {
  return (0).toLocaleString('en-IN', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
}


export function parseIndianAmount(formatted: string): number {
  const numStr = formatted.replace(/,/g, '');
  return parseInt(numStr, 10) || 0;
}

export const IS_SUCCESS_RESPONSE = (result: any) =>
  [200, 201].includes(result?.status || result?.code) && (result?.success ? result?.success : true);

export const catchAsync = (asyncFunction: any, onSuccess: any, onError?: any, callBackFunction?: (e: any) => void) => {
  return async (...args: any[]) => {
    try {
      const result = await asyncFunction(...args);
      if (onSuccess) {
        onSuccess(result, ...args);
      }
      callBackFunction && callBackFunction(result);
    } catch (error) {
      onError && onError();
      console.log("error oin catch");
      let err = error as Error | AxiosError;
      if (axios.isAxiosError(err)) {
        if (err) {
          const status = err.response?.status;
        }
      }
    }
  };
};

export const checkAnyFileToBeUpload = async (obj: any, documentType: string | any): Promise<any> => {
  const uploadedFiles: any = {};
  // console.log({ obj, documentType });
  let currentFile = ""

  try {
    for (const [key, value] of Object.entries(obj)) {
      currentFile = key
      console.log({ key, value, type: typeof value });

      if (Array.isArray(value)) {
        if (value.every((d: any) => typeof d === "object" && !(d instanceof File))) {
          const extract_files_only = value.filter((d: any) => d.file instanceof File)
          const extract_previously_uploaded_only = value.filter((d: any) => typeof d.file === "string")

          console.log({ extract_files_only, extract_previously_uploaded_only });

          const prepareNewArray = await Promise.all(extract_files_only.map((d) => uploadFileToServer(d.file, d.type)));
          uploadedFiles[key] = [...extract_previously_uploaded_only, ...prepareNewArray];
        } else {
          const filesToUpload = value.filter((item) => item instanceof File);
          const filesPrviouslyUploaded = value.filter((item) => typeof item === "string");

          let newFiles: any = [];
          if (filesToUpload.length > 0) {
            console.log({ documentType: typeof documentType == "object" ? documentType[key] : documentType });

            newFiles = await Promise.all(filesToUpload.map((file) => uploadFileToServer(file, typeof documentType == "object" ? documentType[key] : documentType)));
          }
          uploadedFiles[key] = [...newFiles, ...filesPrviouslyUploaded];
        }
      } else if (typeof value === "object") {
        if (value instanceof File) {
          // console.log(key ,documentType );
          uploadedFiles[key] = await uploadFileToServer(value, typeof documentType == "object" ? documentType[key] : documentType);
        }
        else {
          const [processedSubObject, success, error] = await checkAnyFileToBeUpload(
            value,
            documentType
          );
          if (!success) {
            return [obj, false, error];
          }
          uploadedFiles[key] = processedSubObject;
        }
      } else if (value instanceof File) {
        uploadedFiles[key] = await uploadFileToServer(value, typeof documentType == "object" ? documentType[key] : documentType);
      } else {
        uploadedFiles[key] = value;
      }
    }
    return [uploadedFiles || obj, true];
  } catch (error) {
    let err = error as Error | AxiosError | ApiError;
    const status = (err as any).status;;
    console.log("error oin upload", { error });
    if (axios.isAxiosError(err)) {
      if (err) {
        return [obj, false, {
          dataKey: currentFile,
          message: convertToCamelCase(currentFile) + " : " + err.response?.data?.message,
          status
        }];
      }
    }
    else if (err instanceof ApiError) {
      return [obj, false, {
        dataKey: currentFile,
        message: convertToCamelCase(currentFile) + " : " + err.message,
        status
      }];
    }
    return [obj, false, {
      message: convertToCamelCase(currentFile) + " : " + err?.message,
      dataKey: currentFile,
      status
    }];
  }
};

export function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState);
}




export function collectRoutes(routes: RouteConfig[], parentPath = ''): string[] {
  return routes.flatMap(route => {
    const fullPath = parentPath + (route.path.includes("/") ? route.path : "/" + route.path);
    if (route.children && route.children.length > 0) {
      return [fullPath, ...collectRoutes(route.children, fullPath)];
    }
    return [fullPath];
  });
}

export const VALIDATE_FORM = async (form: UseFormReturn, schema: ZodObject) => {
  const fieldsToValidate = schema.keyof().options;
  form.clearErrors(fieldsToValidate);
  const result = await schema.safeParseAsync(form.getValues());
  if (!result.success) {
    result.error.issues.forEach(issue => form.setError(issue.path[0] as any, { type: 'manual', message: issue.message }));
    return false;
  }
  return true
}

export const isDataNull = (resp: ApiResponse) => {
  if ((Array.isArray(resp.data) && resp.data.length == 0) || (Object.keys(resp.data)?.length == 0)) {
    return false
  }
}